import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { Building2, Users } from "lucide-react";
import { signUpSchema } from "@/lib/validations/auth";

export default function EmployerSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
  });
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consents.terms || !consents.privacy) {
      toast.error("Please accept all required consents to continue");
      return;
    }

    setLoading(true);

    try {
      const validatedData = signUpSchema.parse({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: "employer_service_provider"
      });

      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validatedData.full_name,
            company_name: formData.company_name,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        const consentRecords: Database["public"]["Tables"]["consent_records"]["Insert"][] = [
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I accept the Terms of Service",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I have read and understood the Privacy Policy",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
        ];

        await supabase.from("consent_records").insert(consentRecords);

        await supabase.from("user_roles").insert({
          user_id: data.user.id,
          role: "employer_service_provider",
          approval_status: "approved",
        });

        toast.success("Welcome to RePath! Your company account is ready.");
        navigate("/onboarding");
      }
    } catch (error: any) {
      if (error.errors) {
        error.errors.forEach((err: any) => {
          toast.error(err.message);
        });
      } else {
        toast.error(error.message || "Sign up failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="p-6 border-b">
        <div className="flex items-center gap-2">
          <Building2 className="w-8 h-8 text-[hsl(var(--employer-navy))]" />
          <h1 className="text-2xl font-bold text-[hsl(var(--employer-navy))]">RePath</h1>
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex">
        {/* Left Column - Branding & Value Props */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[hsl(var(--employer-navy))] to-[hsl(var(--employer-navy))]/80 text-white p-12 flex-col justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[hsl(var(--employer-navy))]/40"></div>
          
          <div className="absolute inset-0 opacity-20">
            <Users className="absolute top-20 right-20 w-32 h-32" />
            <Users className="absolute bottom-32 left-16 w-24 h-24" />
          </div>

          <div className="relative z-10 space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4">Unlock Untapped Potential</h2>
              <p className="text-lg text-white/90">
                Access a pre-vetted, motivated talent pool ready to contribute to your success.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))] flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Quality Candidates</h3>
                  <p className="text-white/80">Connect with skilled professionals committed to their next chapter.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--employer-teal))] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Diverse Workforce</h3>
                  <p className="text-white/80">Build a team that reflects commitment and resilience.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Signup Form */}
        <div className="flex-1 flex items-center justify-center p-8 md:p-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[hsl(var(--employer-navy))] mb-2">
                Create a Company Account
              </h2>
              <p className="text-muted-foreground">
                Join the network of forward-thinking employers.
              </p>
            </div>

            <form onSubmit={handleSignUp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={(e) =>
                    setFormData({ ...formData, company_name: e.target.value })
                  }
                  placeholder="Your Company Ltd"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Your Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  placeholder="Jane Smith"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Work Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your.name@company.com"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Create a strong password"
                  className="border-border"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Min 8 characters, include uppercase, lowercase, and number
                </p>
              </div>

              {/* Consents */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={consents.terms}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, terms: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/terms" className="text-[hsl(var(--employer-teal))] hover:underline">
                      Terms of Service
                    </a>
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={consents.privacy}
                    onCheckedChange={(checked) =>
                      setConsents({ ...consents, privacy: checked as boolean })
                    }
                    required
                  />
                  <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{" "}
                    <a href="/privacy" className="text-[hsl(var(--employer-teal))] hover:underline">
                      Privacy Policy
                    </a>
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--employer-teal))] text-white hover:bg-[hsl(var(--employer-teal))]/90 font-semibold py-6 text-lg"
              >
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <a href="/employer-auth" className="text-[hsl(var(--employer-navy))] hover:underline font-semibold">
                  Sign In
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
